import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui';

/*
 * IstaLabsTeaserSection
 * ---------------------
 * "Innovation & research" teaser strip pointing at /istalabs.
 *
 * Layout:
 *   - Full-bleed section (no Container) so content stretches
 *     edge-to-edge, only responsive horizontal padding.
 *   - 2-column grid on md+: `istalabs_banner.png` signage image on
 *     the LEFT, pitch copy + "Explore IstaLabs →" CTA on the RIGHT.
 *   - Image slides in from FAR left (crosses the section) and text
 *     slides in from the right when the section enters view.
 *   - On mobile the columns stack: image first, text below.
 *
 * Used on:
 *   - / (homepage) — closing teaser before the global ConnectSection
 *   - /about       — inserted between "See all our services" and the
 *                    final "Why teams choose Istatec" pitch
 *
 * The two consumers render this component identically; if you tweak
 * anything here it updates both surfaces automatically.
 */

const ISTALABS_TEASER_IMAGE =
  'https://istatec-assets.istatec.com/istalabs_banner.png';

export const IstaLabsTeaserSection: React.FC = () => (
  <section className="bg-navy-50/60 py-16 md:py-24 lg:py-28 overflow-hidden">
    <div className="site-wide-container">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left column — banner-signage image. Slides in from far-
            left (x: -600) so it visually "arrives" from across the
            page as the visitor scrolls into the section. The outer
            section has `overflow-hidden` so that off-screen journey
            is clipped rather than causing a horizontal scrollbar. */}
        <motion.div
          className="order-1 relative w-full"
          initial={{ opacity: 0, x: -600 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={ISTALABS_TEASER_IMAGE}
            alt="IstaLabs — Idea, Research, Product, AI Labs signage"
            className="w-full h-auto rounded-2xl shadow-[0_25px_60px_-15px_rgba(2,8,20,0.35)]"
            loading="lazy"
          />
        </motion.div>

        {/* Right column — pitch + CTA. Slides in from the right. */}
        <motion.div
          className="order-2 max-w-2xl md:justify-self-end"
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-5">
            Innovation &amp; research
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-[52px] font-bold text-navy-950 leading-[1.16] mb-6 md:mb-8">
            Expertise comes from practice, not slides.
          </h2>
          <div className="space-y-5 text-lg md:text-xl lg:text-xl font-normal text-navy-900 leading-relaxed">
            <p>
              IstaLabs is our innovation practice — where we prototype
              new AI models, test emerging cloud patterns, and stress-
              test frameworks before they show up in mainstream stacks.
              When you engage Istatec, you get a team that&rsquo;s
              already explored what&rsquo;s next.
            </p>
            <p>
              We also offer{' '}
              <span className="font-bold">innovation-as-a-service</span> —
              short, focused engagements that go from idea to working
              prototype in weeks, contextualising emerging tech to your
              business.
            </p>
          </div>
          <div className="mt-8">
            <Link to="/istalabs">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Explore IstaLabs
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
